import * as React from 'react';

import { Close } from '@mint-lib/icons';
import { cn } from '@mint-lib/tw-utils';
import { AlertDialog as ConfirmationDialogPrimitive } from 'radix-ui';

import { Button } from './button.jsx';
import { typographyVariants } from './typography.jsx';
import { buttonVariants, dialogVariants } from './variants.jsx';

function ConfirmationDialog({
  ...props
}: Readonly<React.ComponentProps<typeof ConfirmationDialogPrimitive.Root>>) {
  return (
    <ConfirmationDialogPrimitive.Root data-slot="alert-dialog" {...props} />
  );
}

function ConfirmationDialogTrigger({
  ...props
}: React.ComponentProps<typeof ConfirmationDialogPrimitive.Trigger>) {
  return (
    <ConfirmationDialogPrimitive.Trigger
      data-slot="alert-dialog-trigger"
      {...props}
    />
  );
}

function ConfirmationDialogPortal({
  ...props
}: Readonly<React.ComponentProps<typeof ConfirmationDialogPrimitive.Portal>>) {
  return (
    <ConfirmationDialogPrimitive.Portal
      data-slot="alert-dialog-portal"
      {...props}
    />
  );
}

function ConfirmationDialogOverlay({
  className,
  ...props
}: React.ComponentProps<typeof ConfirmationDialogPrimitive.Overlay>) {
  return (
    <ConfirmationDialogPrimitive.Overlay
      data-slot="alert-dialog-overlay"
      className={cn(
        'fixed inset-0 z-overlay bg-ui-overlay data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0',
        className,
      )}
      {...props}
    />
  );
}

function ConfirmationDialogRoot({
  className,
  ...props
}: React.ComponentProps<typeof ConfirmationDialogPrimitive.Content>) {
  return (
    <ConfirmationDialogPrimitive.Content
      data-slot="alert-dialog-content"
      className={cn(dialogVariants({ size: 's' }), className)}
      {...props}
    />
  );
}

function ConfirmationDialogContent({
  className,
  ...props
}: Readonly<{
  children: React.ReactNode;
  className?: string;
}>) {
  return (
    <div className={cn('flex h-full w-full p-4', className)}>
      {props.children}
    </div>
  );
}

function ConfirmationDialogHeader({
  className,
  showCloseButton = true,
  onClose,
  ...props
}: React.ComponentProps<'div'> & {
  showCloseButton?: boolean;
  onClose?: () => void;
}) {
  return (
    <div
      data-slot="alert-dialog-header"
      className={cn('flex h-14 items-center gap-2 p-4', className)}
      {...props}
    >
      {props.children}
      {showCloseButton && (
        <ConfirmationDialogPrimitive.Cancel
          asChild
          data-slot="dialog-close"
          className={cn('absolute top-5 right-5')}
        >
          <Button variant="plain" icon onClick={onClose}>
            <Close className="fill-tertiary-main" />
          </Button>
        </ConfirmationDialogPrimitive.Cancel>
      )}
    </div>
  );
}

function ConfirmationDialogFooter({
  className,
  ...props
}: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="alert-dialog-footer"
      className={cn(
        'flex h-18 w-full items-center justify-end gap-2 p-4',
        className,
      )}
      {...props}
    />
  );
}

function ConfirmationDialogTitle({
  className,
  ...props
}: React.ComponentProps<typeof ConfirmationDialogPrimitive.Title>) {
  return (
    <ConfirmationDialogPrimitive.Title
      data-slot="alert-dialog-title"
      className={cn(typographyVariants({ variant: 'h3' }), className)}
      {...props}
    />
  );
}

function ConfirmationDialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof ConfirmationDialogPrimitive.Description>) {
  return (
    <ConfirmationDialogPrimitive.Description
      data-slot="alert-dialog-description"
      className={cn(typographyVariants({ variant: 'bodyLong01' }), className)}
      {...props}
    />
  );
}

function ConfirmationDialogAction({
  className,
  color = 'primary',
  ...props
}: React.ComponentProps<typeof ConfirmationDialogPrimitive.Action> & {
  color?: 'primary' | 'warning' | 'error';
}) {
  return (
    <ConfirmationDialogPrimitive.Action
      className={cn(
        buttonVariants({ variant: 'contained', size: 'large', color }),
        className,
      )}
      {...props}
    />
  );
}

function ConfirmationDialogCancel({
  className,
  ...props
}: React.ComponentProps<typeof ConfirmationDialogPrimitive.Cancel>) {
  return (
    <ConfirmationDialogPrimitive.Cancel
      className={cn(
        buttonVariants({ variant: 'text', size: 'large' }),
        className,
      )}
      {...props}
    >
      Cancel
    </ConfirmationDialogPrimitive.Cancel>
  );
}

export {
  ConfirmationDialog,
  ConfirmationDialogPortal,
  ConfirmationDialogOverlay,
  ConfirmationDialogTrigger,
  ConfirmationDialogRoot,
  ConfirmationDialogContent,
  ConfirmationDialogHeader,
  ConfirmationDialogFooter,
  ConfirmationDialogTitle,
  ConfirmationDialogDescription,
  ConfirmationDialogAction,
  ConfirmationDialogCancel,
};
